import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AuthenticationContext } from './AuthenticationContext';
import {
  useGetAssociateEvents,
  useGetAssociateIncomingOpenTransferRequests,
  useGetAssociateOutgoingOpenTransferRequests,
  useQueriesCache,
} from '../hooks';
import {
  Associate,
  HeroConversation,
  StoreOpeningStateEvent,
  VsEventMessage,
  VsTransferEvent,
  ShopperLastOnlineEventData,
  ShopperLastOnlineEventDataDictionary,
} from '../model';
import useWebsocket from 'react-use-websocket';
import { chatActions, chatSelectors, useAppSelector } from '../redux';
import { useDispatch } from 'react-redux';

const wsHost = String(process.env.NX_PUBLIC_WEBSOCKETS_ENDPOINT);

enum AssociateRoomType {
  Availability = 'availability',
  StoreOpenState = 'storeOpenState',
  ConversationUpdates = 'conversationUpdates',
  IncomingTransferRequests = 'incomingTransferRequests',
  AssociateEvents = 'associateEvents',
  AssociateUserUpdates = 'associateUserUpdates',
}

const getAssociateRooms = (
  associate: Associate,
): Record<AssociateRoomType, string> => {
  const { retailer, id } = associate;
  const associateAvailabilityRoomId = `${retailer}/associate/${id}/availability`;
  const associateStoreOpenStateRoomId = `${retailer}/associate/${id}/storeOpenState`;
  const associateConversationUpdatesRoomId = `${retailer}/conversation/active/associate/${id}`;
  const associateIncomingTransferRequestsRoomId = `${retailer}/transfer-request/associate/${id}`;
  const associateUserUpdatesRoomId = `${retailer}/associate/${id}`;
  const associateEventsRoomId = `${retailer}/associate-event/associate/${id}`;
  return {
    [AssociateRoomType.AssociateEvents]: associateEventsRoomId,
    [AssociateRoomType.Availability]: associateAvailabilityRoomId,
    [AssociateRoomType.ConversationUpdates]: associateConversationUpdatesRoomId,
    [AssociateRoomType.IncomingTransferRequests]:
      associateIncomingTransferRequestsRoomId,
    [AssociateRoomType.StoreOpenState]: associateStoreOpenStateRoomId,
    [AssociateRoomType.AssociateUserUpdates]: associateUserUpdatesRoomId,
  };
};

const getTransferUpdatesRoom = (transferRequest: VsTransferEvent) =>
  `${transferRequest.retailerId}/transfer-request/${transferRequest.id}`;

const getShopperEventsRoom = ({
  shopperId,
  retailerId,
}: {
  shopperId: string;
  retailerId: string;
}) => `${retailerId}/shopper-event/${shopperId}`;

type State = {
  isConnected: boolean;
  outgoingTransfersUpdateEvents: VsTransferEvent[];
  incomingTransfersUpdateEvents: VsTransferEvent[];
  listenToShopperEvents: (payload: {
    shopperId: string;
    retailerId: string;
  }) => void;
  shopperLastOnlineTimestampEventDictionary?: ShopperLastOnlineEventDataDictionary | null;
  listenToShopperLastOnlineTimestampEvents: (payload: {
    shopperId: string;
    retailerId: string;
  }) => void;
};

const VsWebsocketContext = createContext<State>({} as State);

export const VsWebsocketProvider: FC<PropsWithChildren> = ({ children }) => {
  const joinedRoomsRef = useRef<string[]>([]);

  const {
    updateActiveHeroConversation,
    invalidateStoreOpeningHours,
    invalidateAssociate,
    invalidateShopperEvents,
  } = useQueriesCache();

  const {
    token,
    user: associate,
    isAuthenticated,
  } = useContext(AuthenticationContext);
  const getAssociateEventsQuery = useGetAssociateEvents();
  const {
    data: incomingTransferRequests,
    refetch: refetchIncomingTransferRequests,
  } = useGetAssociateIncomingOpenTransferRequests();
  const dispatch = useDispatch();
  const selectedConversation = useAppSelector(
    chatSelectors.selectSelectedConversation,
  );
  const selectedConversationId = selectedConversation?.id;
  const [incomingTransfersUpdateEvents, setIncomingTransfersUpdateEvents] =
    useState<VsTransferEvent[]>([]);
  const [outgoingTransfersUpdateEvents, setOutgoingTransfersUpdateEvents] =
    useState<VsTransferEvent[]>([]);
  const { data: outgoingTransferRequests } =
    useGetAssociateOutgoingOpenTransferRequests();

  const [isConnected, setIsConnected] = useState<boolean>(false);
  const reconnectAttemptedRef = useRef<boolean>(false);

  const [
    shopperLastOnlineTimestampEventDictionary,
    setShopperLastOnlineTimestampEventDictionary,
  ] = useState<ShopperLastOnlineEventDataDictionary | null>(null);

  const onOpen = () => setIsConnected(true);
  const onClose = () => setIsConnected(false);

  const joinRoom = (roomId: string, rejoin?: boolean) => {
    const sendWsJoinMessage = (roomId: string) =>
      sendJsonMessage({
        action: 'join-room',
        roomId,
      });
    if (rejoin) {
      sendWsJoinMessage(roomId);
      return;
    }
    if (!joinedRoomsRef.current.includes(roomId)) {
      sendWsJoinMessage(roomId);
      joinedRoomsRef.current = [...joinedRoomsRef.current, roomId];
    }
  };

  useEffect(() => {
    //join all associate rooms on first connection
    if (associate && isConnected && !reconnectAttemptedRef.current) {
      const associateRooms = getAssociateRooms(associate);
      Object.values(associateRooms).forEach((room) =>
        joinRoom(room as AssociateRoomType),
      );
    }
  }, [associate, isConnected]);

  useEffect(() => {
    //rejoin all previously joined rooms on reconnection
    if (isConnected && reconnectAttemptedRef.current) {
      joinedRoomsRef.current.forEach((room) =>
        joinRoom(room as AssociateRoomType, true),
      );
    }
  }, [isConnected]);

  const listenToTransferRequestUpdates = (transferRequest: VsTransferEvent) => {
    joinRoom(getTransferUpdatesRoom(transferRequest));
  };

  const listenToShopperEvents = (payload: {
    shopperId: string;
    retailerId: string;
  }) => {
    joinRoom(getShopperEventsRoom(payload));
  };

  const listenToShopperLastOnlineTimestampEvents = ({
    shopperId,
    retailerId,
  }: {
    shopperId: string;
    retailerId: string;
  }) => {
    const roomId = `${retailerId}/shopper/${shopperId}/last-online`;
    if (!joinedRoomsRef.current.includes(roomId)) {
      joinRoom(roomId);
    }
  };

  useEffect(() => {
    if (incomingTransferRequests?.length) {
      listenToTransferRequestUpdates(incomingTransferRequests[0]);
    }
  }, [incomingTransferRequests]);

  useEffect(() => {
    outgoingTransferRequests?.forEach((transferRequest) => {
      listenToTransferRequestUpdates(transferRequest);
    });
  }, [outgoingTransferRequests]);

  const handleAssociateEventMessage = (event: VsEventMessage) => {
    if (!associate) {
      return;
    }
    const associateRooms = getAssociateRooms(associate);
    const associateRoomType = Object.keys(associateRooms).find(
      (associateRoomType) =>
        associateRooms[associateRoomType as AssociateRoomType] === event.roomId,
    );

    if (
      associateRoomType === AssociateRoomType.Availability ||
      associateRoomType === AssociateRoomType.AssociateUserUpdates
    ) {
      invalidateAssociate();
    }

    if (associateRoomType === AssociateRoomType.AssociateEvents) {
      getAssociateEventsQuery.refetch();
    }

    if (associateRoomType === AssociateRoomType.ConversationUpdates) {
      const updatedConversation = event.data as HeroConversation;
      updateActiveHeroConversation(updatedConversation);
      if (selectedConversationId === updatedConversation.id) {
        dispatch(chatActions.setSelectedConversation(updatedConversation));
      }
    }

    if (associateRoomType === AssociateRoomType.IncomingTransferRequests) {
      refetchIncomingTransferRequests();
    }

    if (associateRoomType === AssociateRoomType.StoreOpenState) {
      invalidateStoreOpeningHours(
        (event.data as StoreOpeningStateEvent).storeId,
      );
    }
  };

  const handleTransferUpdateEventMessage = (event: VsEventMessage) => {
    if (
      new RegExp(`^${associate?.retailer}/transfer-request/(.*)$`).test(
        event.roomId,
      )
    ) {
      //if the creator is the loggied in associate, it's an outgoing request, incoming otherwise
      const transferUpdateEvent = {
        ...event.data,
        creation: new Date().toISOString(),
      } as VsTransferEvent;
      if (associate?.id === (event.data as VsTransferEvent).creator) {
        setOutgoingTransfersUpdateEvents([
          ...outgoingTransfersUpdateEvents,
          transferUpdateEvent,
        ]);
      } else {
        setIncomingTransfersUpdateEvents([
          ...incomingTransfersUpdateEvents,
          transferUpdateEvent,
        ]);
      }
    }
  };

  const handleShopperEventMessage = (event: VsEventMessage) => {
    const shopperEventRegexMatches =
      event.roomId && event.roomId.match(new RegExp(/\/shopper-event\/(.*)$/));
    if (shopperEventRegexMatches?.[1]) {
      const shopperId = shopperEventRegexMatches?.[1];
      invalidateShopperEvents(shopperId);
    }
  };

  const handleShopperLastOnlineTimestampEventMessage = (
    event: VsEventMessage,
  ) => {
    // shopper timestamp event received
    const shopperLastOnlineTimestampEventRegexMatches =
      event.roomId &&
      event.roomId.match(new RegExp(/\/shopper\/(.*)\/last-online$/));
    if (shopperLastOnlineTimestampEventRegexMatches?.[1]) {
      const shopperId = shopperLastOnlineTimestampEventRegexMatches?.[1];
      setShopperLastOnlineTimestampEventDictionary({
        ...shopperLastOnlineTimestampEventDictionary,
        [shopperId]: (event.data as ShopperLastOnlineEventData)
          .lastOnlineTimestamp,
      });
    }
  };

  const onMessage = (event: MessageEvent<string>) => {
    const message = JSON.parse(event.data) as VsEventMessage;
    handleAssociateEventMessage(message);
    handleShopperEventMessage(message);
    handleTransferUpdateEventMessage(message);
    handleShopperLastOnlineTimestampEventMessage(message);
  };

  const { sendJsonMessage } = useWebsocket(
    wsHost,
    {
      queryParams: {
        Token: token,
      },
      onMessage,
      onOpen,
      onClose,
      retryOnError: true,
      shouldReconnect: () => {
        reconnectAttemptedRef.current = true;
        return true;
      },
      reconnectAttempts: 10,
      reconnectInterval: 3000,
      heartbeat:
        associate && isAuthenticated
          ? {
              message: JSON.stringify({
                action: 'join-room',
                //as agreed with the backend team, we will emulate a heartbeart message by rejoining the associate availability room,
                //this is a workaround, because we don't have a dedicated room for sending heartbeat messages
                roomId:
                  getAssociateRooms(associate)[AssociateRoomType.Availability],
              }),
              timeout: 10000000, //timeout needs to be set to an unreachable value, as we won't receive a response for the "fake" heartbeat message
              interval: 30000, // send the heartbeat message every 30 seconds
            }
          : undefined,
    },
    Boolean(token) && Boolean(associate) && isAuthenticated,
  );

  return (
    <VsWebsocketContext.Provider
      value={{
        isConnected,
        incomingTransfersUpdateEvents,
        outgoingTransfersUpdateEvents,
        shopperLastOnlineTimestampEventDictionary,
        listenToShopperEvents,
        listenToShopperLastOnlineTimestampEvents,
      }}
    >
      {children}
    </VsWebsocketContext.Provider>
  );
};

export const useVsWebsocket = () => useContext(VsWebsocketContext);
